import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getPublicPageMeta } from '../constants/publicPageMeta';
import { usePageViewTracking } from './usePageViewTracking';

/** Set document title + meta description for the current route; restore on unmount. */
export function usePageMeta(title: string | undefined, description: string | undefined) {
  useEffect(() => {
    if (!title || !description) return;

    const previousTitle = document.title;
    const descriptionMeta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const previousDescription = descriptionMeta?.getAttribute('content') ?? '';

    const applyMeta = () => {
      document.title = title;
      if (descriptionMeta) {
        descriptionMeta.setAttribute('content', description);
      }
    };

    applyMeta();

    // Builder.io content can overwrite document.title after RouteMeta runs — keep SEO copy.
    const titleEl = document.querySelector('title');
    let observer: MutationObserver | null = null;
    if (titleEl) {
      observer = new MutationObserver(() => {
        if (document.title !== title) applyMeta();
      });
      observer.observe(titleEl, { childList: true, characterData: true, subtree: true });
    }

    return () => {
      observer?.disconnect();
      document.title = previousTitle;
      if (descriptionMeta) {
        descriptionMeta.setAttribute('content', previousDescription);
      }
    };
  }, [title, description]);
}

/** Applies SEO meta + page view tracking for the current route. */
export function RouteMeta() {
  const { pathname } = useLocation();
  const meta = getPublicPageMeta(pathname);
  usePageMeta(meta?.title, meta?.description);
  usePageViewTracking();
  return null;
}
